import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  isPinRequiredResponse,
  pinUnlockHeaders,
} from "../../utils/pinUnlockSession";
import {
  ScaleTable,
  expenseFields,
  isRecurringBonus,
  normalizeScaleLines,
  upfrontFields,
} from "./payEditorPieces";
import {
  BONUS_CATEGORY_LABELS,
  EXPENSE_CATEGORY_LABELS,
  ORANGE,
  UPFRONT_PAY_TYPE_LABELS,
  currency,
  titleize,
} from "./repDealDisplay";
import AddUpfrontPayForm from "../Organization/AddUpfrontPayForm";
import AddBonusForm from "../Organization/AddBonusForm";
import AddExpenseForm from "../Organization/AddExpenseForm";
import OverrideTab from "./OverrideTab";

// The pay editor: every pay component (Commission, Marketing Deal, Override,
// Bonuses, Upfront, Expenses) shown at once in a two-per-row grid, each edited
// in place. Empty sections show a blank "add" card. Saves the whole structure
// at once through the same endpoints the profile Pay tab uses —
// POST /v1/ps/user-deal/for-rep/:repId to create a document-less active deal
// when the rep has none, or PUT /v1/ps/user-deal/:id to replace the pay of the
// one they already have. Single-rep (non-split) deals only; splits stay in the
// Add Deal wizard.
//
// `deal` is the current deal from the by-rep read (or null); `onSaved` reloads
// the parent once a save lands; `onPinRequired` re-locks when the grant lapses.
function RepPayStructureEditor({
  repId,
  repName,
  deal,
  sellingYearId,
  onSaved,
  onCancel,
  onPinRequired,
}) {
  const { getVerifiedJWT } = useAuth();

  const dealId = deal?.deal_id ?? null;
  const dealUsers = Array.isArray(deal?.deal_users) ? deal.deal_users : [];
  const isSplit = dealUsers.length > 1;

  const idRef = useRef(0);
  const nextId = () => `item-${(idRef.current += 1)}`;

  const lineArray = scale =>
    Array.isArray(scale?.scale_lines) && scale.scale_lines.length
      ? scale.scale_lines.map(l => ({
          revenue_amount: l.revenue_amount,
          percentage: l.percentage,
        }))
      : [{ revenue_amount: 0, percentage: 0 }];

  const scaleToPeriod = scale =>
    scale && (scale.effective_start_date || scale.effective_end_date)
      ? {
          start_date: scale.effective_start_date || null,
          end_date: scale.effective_end_date || null,
        }
      : null;

  // "Added" = the section is showing its editor. It starts open when the deal
  // already has that scale, and otherwise shows a blank "add" card.
  const [commissionAdded, setCommissionAdded] = useState(
    (dealUsers[0]?.commission_scale?.scale_lines || []).length > 0
  );
  const [commissionLines, setCommissionLines] = useState(
    lineArray(dealUsers[0]?.commission_scale)
  );
  // Overrides live in their own section (peer overrides), not on the deal.
  const [mdAdded, setMdAdded] = useState(!!deal?.md_scale);
  const [mdLines, setMdLines] = useState(lineArray(deal?.md_scale));
  const [mdPeriod, setMdPeriod] = useState(scaleToPeriod(deal?.md_scale));

  const [upfront, setUpfront] = useState(dealUsers[0]?.upfront_pay ?? null);

  const [bonuses, setBonuses] = useState(() =>
    (dealUsers[0]?.default_bonuses || []).map(b => ({ _id: nextId(), ...b }))
  );

  const [expenses, setExpenses] = useState(() =>
    (deal?.default_expenses || []).map(e => ({ _id: nextId(), ...e }))
  );

  // The "Add Pay Details" flow: a type picker, then a per-type modal.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addType, setAddType] = useState(null); // commission | md | override | upfront | bonus | expense
  // The person-specific (peer) overrides this rep earns — listed as a section
  // of the deal so they read alongside the scales, even though they are stored
  // separately from the deal itself.
  const [peerOverrides, setPeerOverrides] = useState([]);
  // When set, the bonus/expense/upfront config modal is editing an existing item
  // rather than adding a new one — the form seeds from it and the save replaces
  // it in place (by _id) instead of appending.
  const [editingItem, setEditingItem] = useState(null);
  const [draftLines, setDraftLines] = useState([
    { revenue_amount: 0, percentage: 0 },
  ]);
  const [draftPeriod, setDraftPeriod] = useState(null);

  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // The overview feeds the add forms their company pay-type catalogs.
  const authedFetch = useCallback(
    async (path, options = {}) => {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      return fetch(getApiEndpoint(path), {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
          ...pinUnlockHeaders(),
          ...(options.headers || {}),
        },
      });
    },
    [getVerifiedJWT]
  );

  const loadPeerOverrides = useCallback(async () => {
    if (!repId) return;
    try {
      const res = await authedFetch(
        `/v1/ps/peer-overrides?involving_rep_id=${repId}`
      );
      if (!res.ok) return;
      const rows = (await res.json())?.data || [];
      // Only the overrides this rep earns (recipient), not ones where they are
      // the target of someone else's override.
      setPeerOverrides(
        rows.filter(r => String(r.recipient?.rep_id) === String(repId))
      );
    } catch {
      // The list is informational; the deal still saves without it.
    }
  }, [authedFetch, repId]);

  useEffect(() => {
    loadPeerOverrides();
  }, [loadPeerOverrides]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch(
          sellingYearId
            ? `/v1/ps/overview?selling_year_id=${sellingYearId}`
            : "/v1/ps/overview"
        );
        if (res.ok) {
          const body = await res.json();
          if (!cancelled) setOverview(body?.data ?? null);
        }
      } catch {
        // The add forms fall back to their built-in type catalogs.
      } finally {
        if (!cancelled) setLoadingOverview(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch, sellingYearId]);

  const hasScale = lines =>
    Array.isArray(lines) && lines.some(l => Number(l.percentage) > 0);

  // A filled-in Marketing Deal scale makes this a marketing deal; otherwise
  // it's a rep deal. No explicit toggle — the sections you fill in decide.
  const isMd = mdAdded && hasScale(mdLines);
  const hasCommission = commissionAdded && hasScale(commissionLines);

  const save = async () => {
    setError(null);

    if (!isMd && !hasCommission) {
      setError("Add a commission scale or a marketing-deal scale first.");
      return;
    }

    const payload = {
      deal_type: isMd ? "MD" : "REP",
      // Overrides are managed in their own section as peer overrides, not on
      // the deal itself.
      allow_override: false,
      users: [
        {
          commission_scale_lines: hasCommission
            ? normalizeScaleLines(commissionLines)
            : [],
          upfront_pay: upfront ?? undefined,
          bonuses: bonuses.map(({ _id, ...b }) => b),
        },
      ],
      expenses: expenses.map(({ _id, ...e }) => e),
    };
    if (isMd) {
      payload.md_scale_lines = normalizeScaleLines(mdLines);
      if (mdPeriod) payload.md_scale_period = mdPeriod;
    }
    if (!dealId && sellingYearId) {
      payload.selling_year_id = Number(sellingYearId);
    }

    try {
      setSaving(true);
      const res = await authedFetch(
        dealId
          ? `/v1/ps/user-deal/${dealId}`
          : `/v1/ps/user-deal/for-rep/${repId}`,
        { method: dealId ? "PUT" : "POST", body: JSON.stringify(payload) }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (isPinRequiredResponse(res, body)) {
          onPinRequired?.();
          return;
        }
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      onSaved?.();
    } catch (err) {
      setError(err.message || "Failed to save the pay structure");
    } finally {
      setSaving(false);
    }
  };

  if (isSplit) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="gray" variant="light">
        This is a split deal with multiple parties. Split deals are edited
        through the Add Deal flow.
      </Alert>
    );
  }

  if (loadingOverview) {
    return (
      <Center py="lg">
        <Loader />
      </Center>
    );
  }

  // A bare section (no card): a centered title, a Remove control, and content.
  // One upfront / bonus / expense item as a card in the same format as the
  // scale tables: white title on top, then an orange "Details / Amount" header,
  // then grey/white rows with the amount on the right.
  const payItemCard = ({
    key,
    title,
    subtitle,
    onEdit,
    onRemove,
    fields,
    col1Label = "Details",
    benchmark,
  }) => (
    <Card
      key={key}
      withBorder
      radius="md"
      p={0}
      style={{
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Group
        justify="space-between"
        align="center"
        px="sm"
        py="xs"
        wrap="nowrap"
      >
        <Stack gap={0} style={{ flex: 1 }}>
          <Text fw={700} size="sm">
            {title}
          </Text>
          {subtitle && (
            <Text size="xs" c="dimmed">
              {subtitle}
            </Text>
          )}
        </Stack>
        {(onEdit || onRemove) && (
          <Group gap={4} wrap="nowrap">
            {onEdit && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={onEdit}
                aria-label="Edit"
              >
                <IconPencil size={15} />
              </ActionIcon>
            )}
            {onRemove && (
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={onRemove}
                aria-label="Remove"
              >
                <IconTrash size={15} />
              </ActionIcon>
            )}
          </Group>
        )}
      </Group>
      <Group gap={0} style={{ background: ORANGE }}>
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          c="white"
          size="sm"
          py={6}
        >
          {col1Label}
        </Text>
        <Divider orientation="vertical" color="rgba(255,255,255,0.4)" />
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          c="white"
          size="sm"
          py={6}
        >
          Amount
        </Text>
      </Group>
      <Box
        style={{
          flex: 1,
          background: "var(--mantine-color-gray-1)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {benchmark ? (
          <>
            <Divider />
            <Group gap={0} wrap="nowrap" style={{ flex: 1 }} align="stretch">
              <Stack
                gap={0}
                style={{ flex: 1, justifyContent: "center" }}
                px="xs"
                py={6}
              >
                <Text ta="center" size="sm" fw={600}>
                  {benchmark.value}
                </Text>
                {benchmark.type && (
                  <Text ta="center" size="xs" c="dimmed">
                    {benchmark.type}
                  </Text>
                )}
              </Stack>
              <Divider orientation="vertical" />
              <Box
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                px="xs"
                py={6}
              >
                <Text ta="center" size="sm" fw={600}>
                  {benchmark.amount}
                </Text>
              </Box>
            </Group>
          </>
        ) : (
          (fields || []).map((field, i) => {
            const value = field.onChange
              ? field.unit === "money"
                ? currency(field.value)
                : field.unit === "percent"
                  ? `${field.value ?? 0}%`
                  : (field.value ?? "")
              : field.text;
            return (
              <React.Fragment key={`f-${i}`}>
                <Divider />
                <Group
                  gap={0}
                  wrap="nowrap"
                  style={{ flex: 1 }}
                  align="stretch"
                >
                  <Box
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    px="xs"
                    py={6}
                  >
                    <Text ta="center" size="sm" c="dimmed">
                      {field.label}
                    </Text>
                  </Box>
                  <Divider orientation="vertical" />
                  <Box
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    px="xs"
                    py={6}
                  >
                    <Text ta="center" size="sm" fw={600}>
                      {value}
                    </Text>
                  </Box>
                </Group>
              </React.Fragment>
            );
          })
        )}
      </Box>
    </Card>
  );

  // A bonus rendered as a single "Benchmark → Amount" row: the earning
  // threshold (revenue / accounts / recruits) with its metric type beneath it on
  // the left, and the bonus payout on the right.
  const bonusBenchmark = b => {
    const payout = isRecurringBonus(b.bonus_type_category)
      ? (b.recurring_repeated_amount ?? b.recurring_initial_amount)
      : b.bonus_type_category === "ITEM"
        ? b.item_monetary_value
        : b.bonus_amount;

    let value;
    switch (b.bonus_type_earned_by_item) {
      case "REVENUE":
        value = currency(b.bonus_revenue_amount);
        break;
      case "ACCOUNTS":
        value = `${b.bonus_accounts_amount ?? 0} accounts`;
        break;
      case "RECRUITS":
        value = `${b.bonus_recruits_amount ?? 0} recruits`;
        break;
      default:
        value = "—";
    }

    return {
      value,
      type: b.bonus_type_earned_by_type
        ? titleize(b.bonus_type_earned_by_type)
        : "",
      amount: currency(payout),
    };
  };

  // The pay-detail types that can still be added (single-instance ones drop off
  // the list once present; bonuses, expenses and overrides can always be added).
  const addableTypes = [
    !commissionAdded && { value: "commission", label: "Commission Scale" },
    !mdAdded && { value: "md", label: "Marketing Deal Scale" },
    { value: "override", label: "Override" },
    !upfront && { value: "upfront", label: "Upfront Pay" },
    { value: "bonus", label: "Bonus" },
    { value: "expense", label: "Expense" },
  ].filter(Boolean);

  const choose = type => {
    setPickerOpen(false);
    setEditingItem(null);
    if (type === "commission" || type === "md") {
      setDraftLines([{ revenue_amount: 0, percentage: 0 }]);
      setDraftPeriod(null);
    }
    setAddType(type);
  };
  const closeAdd = () => {
    if (addType === "override") loadPeerOverrides();
    setAddType(null);
    setEditingItem(null);
  };

  // The pencil on a card: re-open that item's config modal seeded with its
  // current values so the pay type / details can be changed (amounts are also
  // editable inline on the card). Scales open with their lines in the draft;
  // upfront/bonus/expense open their forms with `initial` set.
  const openEdit = (type, item) => {
    if (type === "commission") {
      setDraftLines(commissionLines);
      setDraftPeriod(null);
    } else if (type === "md") {
      setDraftLines(mdLines);
      setDraftPeriod(mdPeriod);
    } else {
      setEditingItem(item ?? null);
    }
    setAddType(type);
  };

  const nothingAdded =
    !commissionAdded &&
    !mdAdded &&
    !upfront &&
    bonuses.length === 0 &&
    expenses.length === 0;

  return (
    <Stack gap="lg">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      {/* Added pay details, shown bare (no cards), two per row. */}
      <SimpleGrid cols={1} spacing="lg" verticalSpacing="lg">
        {commissionAdded && (
          <ScaleTable
            key="commission"
            title="Commission Scale"
            col2Label="Commission"
            lines={commissionLines}
            onChange={setCommissionLines}
            readOnly
            onEdit={() => openEdit("commission")}
            onRemove={() => {
              setCommissionAdded(false);
              setCommissionLines([{ revenue_amount: 0, percentage: 0 }]);
            }}
          />
        )}

        {mdAdded && (
          <ScaleTable
            key="md"
            title="Marketing Deal Scale"
            col2Label="MD %"
            lines={mdLines}
            period={mdPeriod}
            readOnly
            onEdit={() => openEdit("md")}
            onRemove={() => {
              setMdAdded(false);
              setMdLines([{ revenue_amount: 0, percentage: 0 }]);
              setMdPeriod(null);
            }}
          />
        )}

        {peerOverrides.length > 0 &&
          payItemCard({
            key: "overrides",
            title: "Overrides",
            subtitle: "Person-specific overrides this rep earns",
            onEdit: () => setAddType("override"),
            col1Label: "Applies to",
            fields: peerOverrides.map(o => {
              const target = o.target_all_recruits
                ? "Every direct recruit"
                : o.target?.name || "—";
              const window =
                o.effective_start_date || o.effective_end_date
                  ? ` (${o.effective_start_date || "start"} to ${
                      o.effective_end_date || "end"
                    })`
                  : "";
              const guarantee =
                o.condition_type === "MARGIN_GUARANTEE" ? " min margin" : "";
              const tiers = (o.scale_lines || [])
                .map(l =>
                  Number(l.revenue_amount) > 0
                    ? `${currency(l.revenue_amount)} → ${Number(l.percentage)}%`
                    : `${Number(l.percentage)}%`
                )
                .join(", ");
              return {
                label: `${target}${window} · funded by ${
                  o.grantor?.name || "next level up"
                }`,
                text: `${tiers}${guarantee}`,
              };
            }),
          })}

        {upfront &&
          payItemCard({
            key: "upfront",
            title:
              UPFRONT_PAY_TYPE_LABELS[upfront.upfront_pay_type] ||
              "Upfront Pay",
            onEdit: () => openEdit("upfront", upfront),
            onRemove: () => setUpfront(null),
            fields: upfrontFields(upfront, next => setUpfront(next)),
          })}

        {bonuses.map(bonus =>
          payItemCard({
            key: bonus._id,
            title: bonus.name || "Bonus",
            subtitle:
              BONUS_CATEGORY_LABELS[bonus.bonus_type_category] ||
              titleize(bonus.bonus_type_category),
            onEdit: () => openEdit("bonus", bonus),
            onRemove: () =>
              setBonuses(prev => prev.filter(b => b._id !== bonus._id)),
            col1Label: "Benchmark",
            benchmark: bonusBenchmark(bonus),
          })
        )}

        {expenses.map(expense =>
          payItemCard({
            key: expense._id,
            title: expense.name || "Expense",
            subtitle:
              EXPENSE_CATEGORY_LABELS[expense.expense_type_category] ||
              titleize(expense.expense_type_category),
            onEdit: () => openEdit("expense", expense),
            onRemove: () =>
              setExpenses(prev => prev.filter(e => e._id !== expense._id)),
            fields: expenseFields(expense, next =>
              setExpenses(prev =>
                prev.map(e =>
                  e._id === expense._id ? { ...next, _id: expense._id } : e
                )
              )
            ),
          })
        )}
      </SimpleGrid>

      {/* The single dotted "Add Pay Details" box. */}
      <UnstyledButton
        onClick={() => setPickerOpen(true)}
        style={{
          display: "block",
          width: "100%",
          border: "2px dashed #e5e7eb",
          borderRadius: "10px",
          padding: nothingAdded ? "40px 16px" : "20px 16px",
          background: "#fafafa",
        }}
      >
        <Group justify="center" gap={6}>
          <IconPlus size={18} color="#d0521f" />
          <Text fw={600} size="sm" c="#d0521f">
            Add Pay Details
          </Text>
        </Group>
      </UnstyledButton>

      <Group justify="flex-end" gap="sm">
        {onCancel && (
          <Button variant="default" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button color="orange" onClick={save} loading={saving}>
          {dealId ? "Save pay structure" : "Create pay structure"}
        </Button>
      </Group>

      {/* Step 1: pick what to add. */}
      <Modal
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Add Pay Details"
        centered
      >
        <SimpleGrid cols={2} spacing="sm">
          {addableTypes.map(t => (
            <Button
              key={t.value}
              variant="light"
              color="orange"
              onClick={() => choose(t.value)}
            >
              {t.label}
            </Button>
          ))}
        </SimpleGrid>
      </Modal>

      {/* Step 2: configure it. */}
      <Modal
        opened={addType === "commission"}
        onClose={closeAdd}
        title={
          commissionAdded ? "Edit Commission Scale" : "Add Commission Scale"
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          <ScaleTable
            title="Commission Scale"
            col2Label="Commission"
            lines={draftLines}
            onChange={setDraftLines}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeAdd}>
              Cancel
            </Button>
            <Button
              color="orange"
              onClick={() => {
                setCommissionLines(draftLines);
                setCommissionAdded(true);
                closeAdd();
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={addType === "md"}
        onClose={closeAdd}
        title={
          mdAdded ? "Edit Marketing Deal Scale" : "Add Marketing Deal Scale"
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          <ScaleTable
            title="Marketing Deal Scale"
            col2Label="MD %"
            lines={draftLines}
            onChange={setDraftLines}
            period={draftPeriod}
            onPeriodChange={setDraftPeriod}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeAdd}>
              Cancel
            </Button>
            <Button
              color="orange"
              onClick={() => {
                setMdLines(draftLines);
                setMdPeriod(draftPeriod);
                setMdAdded(true);
                closeAdd();
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={addType === "override"}
        onClose={closeAdd}
        title="Override"
        size="lg"
        centered
      >
        <OverrideTab
          repId={repId}
          repName={repName}
          sellingYearId={sellingYearId}
          onPinRequired={onPinRequired}
        />
      </Modal>

      <Modal
        opened={addType === "upfront"}
        onClose={closeAdd}
        title={editingItem ? "Edit Upfront Pay" : "Add Upfront Pay"}
        size="lg"
        centered
      >
        <AddUpfrontPayForm
          key={editingItem ? "edit-upfront" : "add-upfront"}
          initial={editingItem}
          companyUpfrontTypes={overview?.ps_company_upfront_types}
          onCreated={created => {
            setUpfront(created);
            closeAdd();
          }}
          onCancel={closeAdd}
        />
      </Modal>

      <Modal
        opened={addType === "bonus"}
        onClose={closeAdd}
        title={editingItem ? "Edit Bonus" : "Add Bonus"}
        size="lg"
        centered
      >
        <AddBonusForm
          key={editingItem?._id ?? "add-bonus"}
          inline
          initial={editingItem}
          sellingYearId={sellingYearId ? Number(sellingYearId) : null}
          companyBonusTypes={overview?.ps_company_bonus_types}
          onCreated={created => {
            const edited = editingItem;
            setBonuses(prev =>
              edited
                ? prev.map(b =>
                    b._id === edited._id ? { ...created, _id: edited._id } : b
                  )
                : [...prev, { _id: nextId(), ...created }]
            );
            closeAdd();
          }}
          onCancel={closeAdd}
        />
      </Modal>

      <Modal
        opened={addType === "expense"}
        onClose={closeAdd}
        title={editingItem ? "Edit Expense" : "Add Expense"}
        size="lg"
        centered
      >
        <AddExpenseForm
          key={editingItem?._id ?? "add-expense"}
          inline
          initial={editingItem}
          sellingYearId={sellingYearId ? Number(sellingYearId) : null}
          companyExpenseTypes={overview?.ps_company_expense_types}
          onCreated={created => {
            const edited = editingItem;
            setExpenses(prev =>
              edited
                ? prev.map(e =>
                    e._id === edited._id ? { ...created, _id: edited._id } : e
                  )
                : [...prev, { _id: nextId(), ...created }]
            );
            closeAdd();
          }}
          onCancel={closeAdd}
        />
      </Modal>
    </Stack>
  );
}

export default RepPayStructureEditor;
